// ────────────────────────────────────────────────────────────
// Role → Service mapping admin table
// ────────────────────────────────────────────────────────────
import React from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import MinimalisticTable, {
  ColumnDef,
} from '../../../../shared/components/ui/MinimalisticTable';
import MinimalisticDropDown
  from '../../../../shared/components/ui/MinimalisticDropDown';

import {
  fetchRoles,
  setRoleService,
  type Role,
} from '../api/rbacApi';

import {
  fetchServicesApi,
  type Service,
} from '../../../system/services/api/servicesApi';

/* ——————————————————————————————————————————— */
/* Local row type (Role + nullable FK)          */
/* ——————————————————————————————————————————— */
type Row = Role & { service_id: number | null };

export default function RoleServiceTab() {
  const qc = useQueryClient();

  /* roles */
  const { data: roles = [] } = useQuery<Role[], Error>({
    queryKey : ['rbac', 'roles'],
    queryFn  : fetchRoles,
    staleTime: 60_000,
  });

  /* services */
  const { data: services = [] } = useQuery<Service[], Error>({
    queryKey : ['services'],
    queryFn  : fetchServicesApi,
    staleTime: 300_000,
  });

  /* mutation */
  const mut = useMutation<void, Error, { id_role: number; id_service: number | null }>({
    mutationFn: ({ id_role, id_service }) =>
      setRoleService(id_role, id_service),
    onSuccess : () => qc.invalidateQueries({ queryKey: ['rbac', 'roles'] }),
  });

  /* rows (sorted) */
  const rows: Row[] = React.useMemo(
    () =>
      roles
        .map(r => ({ ...r, service_id: (r as any).service_id ?? null }))
        .sort((a, b) => a.id_role - b.id_role),
    [roles],
  );

  /* columns */
  const columns: ColumnDef<Row>[] = [
    { header: 'ID', accessor: 'id_role' },
    {
      header  : 'Role',
      accessor: 'display_name',
      Cell    : (name: string | null, row) => (
        <span>{name ?? row.role_key}</span>
      ),
    },
    {
      header  : 'Service',
      accessor: 'service_id',
      Cell    : (sid: number | null, row) => (
        <MinimalisticDropDown
          value={sid ?? ''}
          disabled={mut.isPending}
          placeholder="— none —"
          minWidth={180}
          options={services.map(s => ({
            value: s.id_service,
            label: s.service_name,
          }))}
          onChange={v =>
            mut.mutate({
              id_role   : row.id_role,
              id_service: v === '' ? null : Number(v),
            })
          }
        />
      ),
    },
  ];

  /* render */
  return (
    <div className="bg-neutral-900 p-4 rounded-md">
      <h2 className="text-lg font-semibold text-neutral-100 mb-4">
        Role → Service
      </h2>

      <MinimalisticTable
        columns={columns}
        data={rows}
        noDataMessage="No roles found."
        showNewButton={false}
      />

      {mut.isPending && (
        <p className="text-sm text-neutral-400 mt-2">Saving mapping…</p>
      )}
    </div>
  );
}